import RAPIER, {
  type Collider,
  type ColliderDesc,
  type RigidBody,
  type World,
} from '@dimforge/rapier3d-compat';
import * as THREE from 'three';

export type PusherDirection = 'forward' | 'backward';

export type PusherVector3 =
  | Readonly<{ x: number; y: number; z: number }>
  | readonly [number, number, number];

export interface PusherPlatformDimensions {
  readonly width: number;
  readonly depth: number;
  readonly thickness: number;
  readonly faceHeight: number;
  readonly faceDepth: number;
  readonly toothDepth: number;
}

export interface PusherCollisionPartDimensions {
  readonly shape: 'cuboid';
  readonly size: Readonly<{ x: number; y: number; z: number }>;
  readonly halfExtents: Readonly<{ x: number; y: number; z: number }>;
  readonly localCenter: Readonly<{ x: number; y: number; z: number }>;
}

export interface PusherCollisionDimensions {
  readonly deck: PusherCollisionPartDimensions;
  readonly face: PusherCollisionPartDimensions;
}

/**
 * Reused telemetry object. Read it at any time, but copy values if retaining a
 * historical sample because the instance updates it in-place each fixed step.
 */
export interface PusherMotionState {
  /** Fully retracted = 0, fully extended toward machine-front (+Z) = 1. */
  readonly progress: number;
  /** Progress through the current forward or backward leg, before easing. */
  readonly legProgress: number;
  /** Normalized oscillator phase in [0, 2). */
  readonly phase: number;
  readonly direction: PusherDirection;
  /** Kinematic target queued for the next Rapier world step. */
  readonly targetZ: number;
  /** Last body transform observed by syncFromPhysics(). */
  readonly actualZ: number;
  /** Analytic target velocity; positive values move toward machine-front. */
  readonly velocityZ: number;
  /** Average one-way travel speed in world units per second. */
  readonly speed: number;
  readonly stroke: number;
  readonly elapsedSeconds: number;
  readonly completedCycles: number;
  readonly fixedSteps: number;
  readonly moving: boolean;
}

export interface PusherVisualOptions {
  readonly castShadow?: boolean;
  readonly receiveShadow?: boolean;
  readonly chromeColor?: THREE.ColorRepresentation;
  readonly darkChromeColor?: THREE.ColorRepresentation;
  readonly accentColor?: THREE.ColorRepresentation;
}

export interface PusherPlatformOptions extends PusherVisualOptions {
  readonly world: World;
  readonly name?: string;
  readonly id?: string;
  /** Body-center position while fully retracted. Defaults to a deck top at y=2. */
  readonly rearPosition?: PusherVector3;
  readonly dimensions?: Partial<PusherPlatformDimensions>;
  /** Travel toward machine-front (+Z), in world units. */
  readonly stroke?: number;
  /** Average one-way travel speed, in world units per second. Zero pauses it. */
  readonly speed?: number;
  readonly initialProgress?: number;
  readonly initialDirection?: PusherDirection;
  readonly friction?: number;
  readonly restitution?: number;
  readonly collisionGroups?: number;
  readonly solverGroups?: number;
  readonly userData?: Readonly<Record<string, unknown>>;
}

export interface PusherTuning {
  readonly speed?: number;
  readonly stroke?: number;
}

export interface PusherResetOptions {
  readonly progress?: number;
  readonly direction?: PusherDirection;
}

interface MutablePusherMotionState {
  progress: number;
  legProgress: number;
  phase: number;
  direction: PusherDirection;
  targetZ: number;
  actualZ: number;
  velocityZ: number;
  speed: number;
  stroke: number;
  elapsedSeconds: number;
  completedCycles: number;
  fixedSteps: number;
  moving: boolean;
}

const DEFAULT_DIMENSIONS: PusherPlatformDimensions = Object.freeze({
  width: 6.4,
  depth: 1.9,
  thickness: 0.18,
  faceHeight: 0.62,
  faceDepth: 0.14,
  toothDepth: 0.12,
});

const DEFAULT_STROKE = 0.74;
const DEFAULT_SPEED = 0.46;

/**
 * Physics-backed coin-pusher shelf.
 *
 * Fixed update order:
 * `pusher.fixedStep(dt) -> world.step() -> pusher.syncFromPhysics()`.
 * The first call queues a Rapier kinematic target, so contacts receive the
 * platform's synthetic velocity instead of being moved by a visual-only mesh.
 */
export class PusherPlatform {
  readonly world: World;
  readonly group: THREE.Group;
  /** Alias for integrations that call renderable entities `object`. */
  readonly object: THREE.Group;
  readonly body: RigidBody;
  /** Primary horizontal deck collider. */
  readonly collider: Collider;
  readonly deckCollider: Collider;
  readonly faceCollider: Collider;
  readonly colliders: readonly [Collider, Collider];
  readonly dimensions: PusherPlatformDimensions;
  readonly collisionDimensions: PusherCollisionDimensions;

  private readonly rear = new THREE.Vector3();
  private readonly queuedTranslation = { x: 0, y: 0, z: 0 };
  private readonly initialProgress: number;
  private readonly initialDirection: PusherDirection;
  private readonly mutableMotion: MutablePusherMotionState;
  private phase = 0;
  private speedValue: number;
  private strokeValue: number;
  private disposed = false;

  constructor(options: PusherPlatformOptions) {
    this.world = options.world;
    this.dimensions = resolveDimensions(options.dimensions);
    this.collisionDimensions = createCollisionDimensions(this.dimensions);
    this.speedValue = readNonNegative(options.speed ?? DEFAULT_SPEED, 'speed');
    this.strokeValue = readPositive(options.stroke ?? DEFAULT_STROKE, 'stroke');
    this.initialProgress = readProgress(options.initialProgress ?? 0, 'initialProgress');
    this.initialDirection = options.initialDirection ?? 'forward';

    readVector3(
      options.rearPosition ?? [0, 2 - this.dimensions.thickness * 0.5, -1.55],
      this.rear,
    );
    this.phase = phaseForProgress(this.initialProgress, this.initialDirection);

    this.group = createPusherVisual(this.dimensions, options);
    this.group.name = options.name ?? 'Coin Pusher Platform';
    this.object = this.group;

    const initialZ = this.rear.z + this.strokeValue * easedProgress(this.phase);
    const bodyDescriptor = RAPIER.RigidBodyDesc.kinematicPositionBased()
      .setTranslation(this.rear.x, this.rear.y, initialZ)
      .setCanSleep(false)
      .setUserData({
        ...options.userData,
        kind: 'pusher-platform',
        id: options.id ?? 'main-pusher',
        axis: '+z',
      });
    this.body = this.world.createRigidBody(bodyDescriptor);

    const deckDesc = configureCollider(
      RAPIER.ColliderDesc.cuboid(
        this.collisionDimensions.deck.halfExtents.x,
        this.collisionDimensions.deck.halfExtents.y,
        this.collisionDimensions.deck.halfExtents.z,
      ),
      this.collisionDimensions.deck,
      options,
    );
    const faceDesc = configureCollider(
      RAPIER.ColliderDesc.cuboid(
        this.collisionDimensions.face.halfExtents.x,
        this.collisionDimensions.face.halfExtents.y,
        this.collisionDimensions.face.halfExtents.z,
      ),
      this.collisionDimensions.face,
      options,
    );

    this.deckCollider = this.world.createCollider(deckDesc, this.body);
    this.faceCollider = this.world.createCollider(faceDesc, this.body);
    this.collider = this.deckCollider;
    this.colliders = Object.freeze([this.deckCollider, this.faceCollider] as const);

    const progress = easedProgress(this.phase);
    const direction = directionForPhase(this.phase);
    this.mutableMotion = {
      progress,
      legProgress: legProgressForPhase(this.phase),
      phase: this.phase,
      direction,
      targetZ: initialZ,
      actualZ: initialZ,
      velocityZ: velocityForPhase(this.phase, this.speedValue),
      speed: this.speedValue,
      stroke: this.strokeValue,
      elapsedSeconds: 0,
      completedCycles: 0,
      fixedSteps: 0,
      moving: this.speedValue > 0,
    };
    this.queuedTranslation.x = this.rear.x;
    this.queuedTranslation.y = this.rear.y;
    this.queuedTranslation.z = initialZ;
    this.body.setNextKinematicTranslation(this.queuedTranslation);
    this.syncFromPhysics();
    this.group.userData.pusher = this.mutableMotion;
  }

  /** Stable, allocation-free live telemetry view. */
  get motion(): PusherMotionState {
    return this.mutableMotion;
  }

  get speed(): number {
    return this.speedValue;
  }

  set speed(value: number) {
    this.setTuning({ speed: value });
  }

  get stroke(): number {
    return this.strokeValue;
  }

  set stroke(value: number) {
    this.setTuning({ stroke: value });
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  /**
   * Advances exactly one caller-owned fixed step and queues the next physical
   * translation. This method deliberately does not call world.step().
   */
  fixedStep(fixedDeltaSeconds: number): PusherMotionState {
    this.assertActive();
    const dt = readNonNegative(fixedDeltaSeconds, 'fixedDeltaSeconds');
    const phaseAdvance = this.speedValue === 0 ? 0 : (this.speedValue / this.strokeValue) * dt;
    const unwrappedPhase = this.phase + phaseAdvance;
    const completed = Math.floor(unwrappedPhase / 2);
    this.phase = positiveModulo(unwrappedPhase, 2);

    this.mutableMotion.elapsedSeconds += dt;
    this.mutableMotion.completedCycles += completed;
    this.mutableMotion.fixedSteps += 1;
    this.updateQueuedTarget();
    this.body.setNextKinematicTranslation(this.queuedTranslation);
    return this.mutableMotion;
  }

  /** Synchronizes only from Rapier; call immediately after world.step(). */
  syncFromPhysics(): PusherMotionState {
    this.assertActive();
    const translation = this.body.translation();
    const rotation = this.body.rotation();
    this.group.position.set(translation.x, translation.y, translation.z);
    this.group.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
    this.mutableMotion.actualZ = translation.z;
    return this.mutableMotion;
  }

  /** Alias that makes fixed-loop ordering self-documenting. */
  afterPhysicsStep(): PusherMotionState {
    return this.syncFromPhysics();
  }

  setTuning(tuning: PusherTuning): PusherMotionState {
    this.assertActive();
    if (tuning.speed !== undefined) this.speedValue = readNonNegative(tuning.speed, 'speed');
    if (tuning.stroke !== undefined) this.strokeValue = readPositive(tuning.stroke, 'stroke');
    this.updateQueuedTarget();
    this.body.setNextKinematicTranslation(this.queuedTranslation);
    return this.mutableMotion;
  }

  /** Immediately restores a clean physical and visual state. */
  reset(options: PusherResetOptions = {}): PusherMotionState {
    this.assertActive();
    const progress = readProgress(options.progress ?? this.initialProgress, 'progress');
    const direction = options.direction ?? this.initialDirection;
    this.phase = phaseForProgress(progress, direction);
    this.mutableMotion.elapsedSeconds = 0;
    this.mutableMotion.completedCycles = 0;
    this.mutableMotion.fixedSteps = 0;
    this.updateQueuedTarget();
    this.body.setTranslation(this.queuedTranslation, true);
    this.body.setNextKinematicTranslation(this.queuedTranslation);
    return this.syncFromPhysics();
  }

  dispose(removeFromWorld = true): void {
    if (this.disposed) return;
    this.group.removeFromParent();
    disposeVisual(this.group);
    this.group.clear();
    if (removeFromWorld && this.world.bodies.contains(this.body.handle)) {
      this.world.removeRigidBody(this.body);
    }
    this.disposed = true;
  }

  private updateQueuedTarget(): void {
    const progress = easedProgress(this.phase);
    const targetZ = this.rear.z + this.strokeValue * progress;
    this.queuedTranslation.x = this.rear.x;
    this.queuedTranslation.y = this.rear.y;
    this.queuedTranslation.z = targetZ;

    this.mutableMotion.phase = this.phase;
    this.mutableMotion.progress = progress;
    this.mutableMotion.legProgress = legProgressForPhase(this.phase);
    this.mutableMotion.direction = directionForPhase(this.phase);
    this.mutableMotion.targetZ = targetZ;
    this.mutableMotion.velocityZ = velocityForPhase(this.phase, this.speedValue);
    this.mutableMotion.speed = this.speedValue;
    this.mutableMotion.stroke = this.strokeValue;
    this.mutableMotion.moving = this.speedValue > 0;
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('PusherPlatform has been disposed.');
  }
}

export function createPusherPlatform(options: PusherPlatformOptions): PusherPlatform {
  return new PusherPlatform(options);
}

function resolveDimensions(overrides: Partial<PusherPlatformDimensions> | undefined): PusherPlatformDimensions {
  const resolved = {
    width: readPositive(overrides?.width ?? DEFAULT_DIMENSIONS.width, 'dimensions.width'),
    depth: readPositive(overrides?.depth ?? DEFAULT_DIMENSIONS.depth, 'dimensions.depth'),
    thickness: readPositive(overrides?.thickness ?? DEFAULT_DIMENSIONS.thickness, 'dimensions.thickness'),
    faceHeight: readPositive(overrides?.faceHeight ?? DEFAULT_DIMENSIONS.faceHeight, 'dimensions.faceHeight'),
    faceDepth: readPositive(overrides?.faceDepth ?? DEFAULT_DIMENSIONS.faceDepth, 'dimensions.faceDepth'),
    toothDepth: readNonNegative(overrides?.toothDepth ?? DEFAULT_DIMENSIONS.toothDepth, 'dimensions.toothDepth'),
  };
  return Object.freeze(resolved);
}

function createCollisionDimensions(dimensions: PusherPlatformDimensions): PusherCollisionDimensions {
  const faceCollisionDepth = dimensions.faceDepth + dimensions.toothDepth;
  return Object.freeze({
    deck: createCollisionPart(
      dimensions.width,
      dimensions.thickness,
      dimensions.depth,
      0,
      0,
      0,
    ),
    face: createCollisionPart(
      dimensions.width,
      dimensions.faceHeight,
      faceCollisionDepth,
      0,
      dimensions.thickness * 0.5 + dimensions.faceHeight * 0.5,
      dimensions.depth * 0.5 + faceCollisionDepth * 0.5,
    ),
  });
}

function createCollisionPart(
  x: number,
  y: number,
  z: number,
  centerX: number,
  centerY: number,
  centerZ: number,
): PusherCollisionPartDimensions {
  return Object.freeze({
    shape: 'cuboid' as const,
    size: Object.freeze({ x, y, z }),
    halfExtents: Object.freeze({ x: x * 0.5, y: y * 0.5, z: z * 0.5 }),
    localCenter: Object.freeze({ x: centerX, y: centerY, z: centerZ }),
  });
}

function configureCollider(
  descriptor: ColliderDesc,
  dimensions: PusherCollisionPartDimensions,
  options: PusherPlatformOptions,
): ColliderDesc {
  descriptor
    .setTranslation(
      dimensions.localCenter.x,
      dimensions.localCenter.y,
      dimensions.localCenter.z,
    )
    .setFriction(readNonNegative(options.friction ?? 0.72, 'friction'))
    .setRestitution(readNonNegative(options.restitution ?? 0.035, 'restitution'));
  if (options.collisionGroups !== undefined) descriptor.setCollisionGroups(options.collisionGroups);
  if (options.solverGroups !== undefined) descriptor.setSolverGroups(options.solverGroups);
  return descriptor;
}

function createPusherVisual(
  dimensions: PusherPlatformDimensions,
  options: PusherVisualOptions,
): THREE.Group {
  const root = new THREE.Group();
  const platform = new THREE.Group();
  platform.name = 'PusherPlatformVisual';
  const face = new THREE.Group();
  face.name = 'PusherFaceVisual';
  root.add(platform, face);

  const chrome = new THREE.MeshStandardMaterial({
    name: 'Pusher polished chrome',
    color: options.chromeColor ?? '#ccd4d7',
    metalness: 0.96,
    roughness: 0.18,
    envMapIntensity: 1.5,
  });
  const brushed = new THREE.MeshStandardMaterial({
    name: 'Pusher brushed chrome',
    color: '#aeb9bd',
    metalness: 0.91,
    roughness: 0.31,
    envMapIntensity: 1.25,
  });
  const darkChrome = new THREE.MeshStandardMaterial({
    name: 'Pusher groove metal',
    color: options.darkChromeColor ?? '#283137',
    metalness: 0.82,
    roughness: 0.28,
  });
  const accent = new THREE.MeshStandardMaterial({
    name: 'Pusher enamel accent',
    color: options.accentColor ?? '#e2aa31',
    metalness: 0.72,
    roughness: 0.25,
  });

  const deck = new THREE.Mesh(
    new THREE.BoxGeometry(dimensions.width, dimensions.thickness, dimensions.depth),
    brushed,
  );
  deck.name = 'Pusher deck collision silhouette';
  platform.add(deck);

  const topPlate = new THREE.Mesh(
    new THREE.BoxGeometry(dimensions.width - 0.12, 0.026, dimensions.depth - 0.1),
    chrome,
  );
  topPlate.name = 'Polished deck plate';
  topPlate.position.y = dimensions.thickness * 0.5 + 0.014;
  platform.add(topPlate);

  const sideRailGeometry = new THREE.BoxGeometry(0.075, 0.075, dimensions.depth + 0.07);
  for (const side of [-1, 1]) {
    const rail = new THREE.Mesh(sideRailGeometry, chrome);
    rail.name = side < 0 ? 'Left chrome edge' : 'Right chrome edge';
    rail.position.set(side * (dimensions.width * 0.5 - 0.038), dimensions.thickness * 0.5 + 0.038, 0);
    platform.add(rail);
  }

  const grooveCount = Math.max(5, Math.floor(dimensions.depth / 0.18));
  const grooveGeometry = new THREE.BoxGeometry(dimensions.width - 0.28, 0.018, 0.026);
  const grooves = new THREE.InstancedMesh(grooveGeometry, darkChrome, grooveCount);
  grooves.name = 'Machined deck grooves';
  const grooveMatrix = new THREE.Matrix4();
  for (let index = 0; index < grooveCount; index += 1) {
    const z = -dimensions.depth * 0.42 + (index / Math.max(1, grooveCount - 1)) * dimensions.depth * 0.74;
    grooveMatrix.makeTranslation(0, dimensions.thickness * 0.5 + 0.031, z);
    grooves.setMatrixAt(index, grooveMatrix);
  }
  grooves.instanceMatrix.needsUpdate = true;
  platform.add(grooves);

  const faceCenterY = dimensions.thickness * 0.5 + dimensions.faceHeight * 0.5;
  const faceCenterZ = dimensions.depth * 0.5 + dimensions.faceDepth * 0.5;
  const facePlate = new THREE.Mesh(
    new THREE.BoxGeometry(dimensions.width, dimensions.faceHeight, dimensions.faceDepth),
    brushed,
  );
  facePlate.name = 'Pusher vertical face';
  facePlate.position.set(0, faceCenterY, faceCenterZ);
  face.add(facePlate);

  const frontBand = new THREE.Mesh(
    new THREE.BoxGeometry(dimensions.width - 0.18, 0.12, 0.035),
    darkChrome,
  );
  frontBand.name = 'Recessed front band';
  frontBand.position.set(
    0,
    faceCenterY + dimensions.faceHeight * 0.12,
    dimensions.depth * 0.5 + dimensions.faceDepth + 0.019,
  );
  face.add(frontBand);

  const topLip = new THREE.Mesh(
    new THREE.BoxGeometry(dimensions.width + 0.08, 0.07, dimensions.faceDepth + 0.1),
    chrome,
  );
  topLip.name = 'Pusher face top lip';
  topLip.position.set(
    0,
    dimensions.thickness * 0.5 + dimensions.faceHeight - 0.025,
    faceCenterZ + 0.025,
  );
  face.add(topLip);

  const endCapGeometry = new THREE.BoxGeometry(0.09, dimensions.faceHeight + 0.045, dimensions.faceDepth + 0.08);
  for (const side of [-1, 1]) {
    const cap = new THREE.Mesh(endCapGeometry, chrome);
    cap.name = side < 0 ? 'Left face end cap' : 'Right face end cap';
    cap.position.set(side * (dimensions.width * 0.5 - 0.03), faceCenterY, faceCenterZ + 0.02);
    face.add(cap);
  }

  const toothCount = Math.max(8, Math.floor(dimensions.width / 0.34));
  const toothRadius = Math.min(0.12, (dimensions.width / toothCount) * 0.31);
  const toothGeometry = new THREE.ConeGeometry(toothRadius, dimensions.toothDepth * 1.5, 4, 1);
  const teeth = new THREE.InstancedMesh(toothGeometry, accent, toothCount);
  teeth.name = 'Chrome pusher teeth';
  const toothQuaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI * 0.5, 0, Math.PI * 0.25));
  const toothPosition = new THREE.Vector3();
  const toothScale = new THREE.Vector3(1, 1, 1);
  const toothMatrix = new THREE.Matrix4();
  for (let index = 0; index < toothCount; index += 1) {
    const x = -dimensions.width * 0.44 + (index / Math.max(1, toothCount - 1)) * dimensions.width * 0.88;
    toothPosition.set(
      x,
      dimensions.thickness * 0.5 + dimensions.faceHeight * 0.34,
      dimensions.depth * 0.5 + dimensions.faceDepth + dimensions.toothDepth * 0.45,
    );
    toothMatrix.compose(toothPosition, toothQuaternion, toothScale);
    teeth.setMatrixAt(index, toothMatrix);
  }
  teeth.instanceMatrix.needsUpdate = true;
  face.add(teeth);

  const castShadow = options.castShadow ?? true;
  const receiveShadow = options.receiveShadow ?? true;
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    object.castShadow = castShadow;
    object.receiveShadow = receiveShadow;
  });
  return root;
}

function disposeVisual(root: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    geometries.add(object.geometry);
    if (Array.isArray(object.material)) {
      for (const material of object.material) materials.add(material);
    } else {
      materials.add(object.material);
    }
  });
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) material.dispose();
}

function phaseForProgress(progress: number, direction: PusherDirection): number {
  if (direction === 'forward') {
    return Math.acos(1 - 2 * progress) / Math.PI;
  }
  const phase = 1 + Math.acos(2 * progress - 1) / Math.PI;
  return phase >= 2 ? 0 : phase;
}

function easedProgress(phase: number): number {
  const legProgress = legProgressForPhase(phase);
  const easedLeg = 0.5 - 0.5 * Math.cos(Math.PI * legProgress);
  return phase < 1 ? easedLeg : 1 - easedLeg;
}

function velocityForPhase(phase: number, speed: number): number {
  if (speed === 0) return 0;
  const magnitude = 0.5 * Math.PI * speed * Math.sin(Math.PI * legProgressForPhase(phase));
  return phase < 1 ? magnitude : -magnitude;
}

function legProgressForPhase(phase: number): number {
  return phase < 1 ? phase : phase - 1;
}

function directionForPhase(phase: number): PusherDirection {
  return phase < 1 ? 'forward' : 'backward';
}

function positiveModulo(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}

function readVector3(value: PusherVector3, target: THREE.Vector3): THREE.Vector3 {
  if (Array.isArray(value)) return target.set(value[0], value[1], value[2]);
  const vector = value as Readonly<{ x: number; y: number; z: number }>;
  return target.set(vector.x, vector.y, vector.z);
}

function readPositive(value: number, label: string): number {
  if (!Number.isFinite(value) || value <= 0) throw new RangeError(`${label} must be a finite number greater than zero.`);
  return value;
}

function readNonNegative(value: number, label: string): number {
  if (!Number.isFinite(value) || value < 0) throw new RangeError(`${label} must be a finite non-negative number.`);
  return value;
}

function readProgress(value: number, label: string): number {
  if (!Number.isFinite(value)) throw new RangeError(`${label} must be a finite number.`);
  return THREE.MathUtils.clamp(value, 0, 1);
}
